<template>
	<CheckboxGroup
		ref="groupRef"
		v-bind="$attrs"
		:class="['checkbox-group', $attrs.class, bordered ? 'checkbox-group-bordered' : '']"
		:value="value"
		@click.capture="onGroupClickCapture"
		@click="onGroupClickBubble"
		@update:value="onUpdateValue"
	>
		<slot />
	</CheckboxGroup>
</template>

<script>
import { computed, ref, useSlots, Fragment } from 'vue';
import { CheckboxGroup } from 'ant-design-vue';

const collectCheckboxValues = nodes => {
	if (!nodes) {
		return [];
	}
	const list = Array.isArray(nodes) ? nodes : [nodes];
	const acc = [];
	for (const node of list) {
		if (!node) {
			continue;
		}
		if (node.type === Fragment || typeof node.type === 'symbol') {
			if (Array.isArray(node.children)) {
				acc.push(...collectCheckboxValues(node.children));
			}
			continue;
		}
		if (Object.prototype.hasOwnProperty.call(node?.props || {}, 'value')) {
			acc.push(node.props.value);
			continue;
		}
		if (Array.isArray(node.children)) {
			acc.push(...collectCheckboxValues(node.children));
		}
	}
	return acc;
};

export default {
	name: 'CheckboxesComponent',
	components: {
		CheckboxGroup,
	},
	props: {
		value: {
			type: Array,
			default: () => [],
		},
		bordered: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update:value'],
	setup(props, { emit }) {
		const slots = useSlots();
		const groupRef = ref(null);
		const lastClickedIndex = ref(null);

		const checkboxValues = computed(() => {
			return collectCheckboxValues(slots.default ? slots.default() : []);
		});

		const onUpdateValue = values => {
			emit('update:value', values);
		};

		const applyShiftRange = (clickedIndex, event) => {
			const anchorIndex = lastClickedIndex.value;
			const [start, end] = clickedIndex > anchorIndex
				? [anchorIndex, clickedIndex]
				: [clickedIndex, anchorIndex];
			const clickedValue = checkboxValues.value[clickedIndex];
			if (typeof clickedValue === 'undefined') {
				return;
			}
			const current = new Set(props.value || []);
			const nextChecked = !current.has(clickedValue);
			const nextValues = new Set(current);
			for (let index = start; index <= end; index += 1) {
				const checkboxValue = checkboxValues.value[index];
				if (typeof checkboxValue === 'undefined') {
					continue;
				}
				if (nextChecked) {
					nextValues.add(checkboxValue);
				} else {
					nextValues.delete(checkboxValue);
				}
			}
			emit('update:value', Array.from(nextValues));
			if (event) {
				event.preventDefault();
				event.stopPropagation();
			}
			lastClickedIndex.value = clickedIndex;
		};

		const getRootEl = () => groupRef.value?.$el || groupRef.value;

		const getInputs = () => {
			return Array.from(getRootEl()?.querySelectorAll('input.ant-checkbox-input') || []);
		};

		const getClickedIndex = event => {
			const target = event.target?.nodeType === Node.TEXT_NODE
				? event.target.parentElement
				: event.target;
			if (!target || !target.closest) {
				return -1;
			}
			const input = target.classList?.contains('ant-checkbox-input')
				? target
				: target.closest('.ant-checkbox-wrapper')?.querySelector('input.ant-checkbox-input');
			if (!input) {
				return -1;
			}
			return getInputs().indexOf(input);
		};

		const onGroupClickCapture = event => {
			const clickedIndex = getClickedIndex(event);
			if (clickedIndex === -1 || !event.shiftKey || lastClickedIndex.value === null) {
				return;
			}
			applyShiftRange(clickedIndex, event);
		};

		const onGroupClickBubble = event => {
			const clickedIndex = getClickedIndex(event);
			if (clickedIndex === -1 || (event.shiftKey && lastClickedIndex.value !== null)) {
				return;
			}
			lastClickedIndex.value = clickedIndex;
		};

		return {
			groupRef,
			onUpdateValue,
			onGroupClickCapture,
			onGroupClickBubble,
		};
	},
};
</script>
